<template>
    <div class="w-100 h-100 position-relative">
        <AonCard
            :borders="false"
            title="Trending Data Visualization"
            desc="Visualize how the target company compares against the selected contenders."
        >
            <template #header-append>
                <div
                    class="display-flex align-items-end"
                    :class="{ 'opacity-50 pointer-events-none': !everythingDoneLoading }"
                >
                    <AonDropdown
                        class="mr-lg"
                        label="Chart Type"
                        :items="graphDropdownOptions"
                        :init-selection="graphDropdownOptions[0]"
                        style="width: 250px"
                        @select-option="changeGraph"
                    />
                    <AonButton
                        label="Edit Contender List"
                        style="min-width: 230px"
                        text-with-icon
                        :iconOptions="{
                            iconStyle: 'fas',
                            iconName: 'fa-gear',
                        }"
                        @clicked="showCompetitorSelection = true"
                    />
                </div>
            </template>
            <div class="p-xxs">
                <div class="display-flex chart-container">
                    <div
                        class="chart-holder position-relative"
                        :class="{
                            'w-100': activeGraph === 'timeline',
                            'flex-col-6': activeGraph === 'both',
                            'display-none': activeGraph === 'quadrant',
                        }"
                    >
                        <AonCoverLoading
                            :loading="
                                loadingViolin &&
                                (activeGraph === 'timeline' || activeGraph === 'both')
                            "
                            title="Generating Timeline"
                        />
                        <TrendingDataViolin
                            key="trending_violin_chart"
                            class="violin-chart"
                            v-if="competitorViolinChartData.length > 0 && !loadingViolin"
                            :chart-data="competitorViolinChartData"
                        />
                    </div>
                    <div
                        class="chart-holder position-relative"
                        :class="{
                            'w-100': activeGraph === 'quadrant',
                            'flex-col-6': activeGraph === 'both',
                            'display-none': activeGraph === 'timeline',
                        }"
                    >
                        <AonCoverLoading
                            :loading="
                                loadingTimeline &&
                                (activeGraph === 'quadrant' || activeGraph === 'both')
                            "
                            title="Generating Quadrant Chart"
                        />
                        <TimelineXYChartAM
                            class="timeline-chart"
                            v-if="
                                competitorTimelineChartData.length > 0 &&
                                !loadingTimeline &&
                                moat2ProductStore.productAlignmentTopCompetitors
                            "
                            :chart-data="competitorTimelineChartData"
                            :loading="loadingTimeline"
                            :entityId="props.entityId"
                        />
                    </div>
                </div>
                <div
                    v-if="!loadingViolin.value || !loadingTimeline.value"
                    class="w-100 display-flex justify-center flex-wrap mt-md pt-md border-top"
                >
                    <div
                        class="display-flex align-items-center mr-lg mt-sm"
                        v-for="comp in moat2ProductStore.productAlignmentTopCompetitors"
                    >
                        <div class="chart-legend-chip mr-sm" :style="getLegendColor(comp)"></div>
                        <p class="text-small">{{ comp.aonEntityName }}</p>
                    </div>
                </div>
            </div>
        </AonCard>
        <AonCard
            class="mt-md"
            :borders="false"
            title="Benchmark Contenders"
            style="overflow: hidden"
        >
            <div class="table-holder">
                <OutputCompetitorTable
                    :table-data="competitorTableData"
                    :all-table-data="allCompetitorTableData"
                    :loading="competitorsLoading"
                    :everything-done-loading="everythingDoneLoading"
                    :updater="competitorTableUpdater"
                    :pinnedEntities="topCompetitorsEntityPks"
                    :tech-area-ids="techIds"
                    :alignmentRunPk="activeStoreAlignmentPk"
                    @refresh-competitor-table="refreshCompetitorTable"
                />
            </div>
        </AonCard>

        <OutputCompetitorSelection
            v-if="showCompetitorSelection"
            :table-data="competitorTableData"
            :all-table-data="allCompetitorTableData"
            :loading="competitorsLoading"
            :everything-done-loading="everythingDoneLoading"
            :updater="competitorTableUpdater"
            :defaultGroupByUltimateToggle="groupByUltimateParent"
            @show-competitor-modal="showCompetitorSelection = false"
            @refresh-arena-space="refreshSpace"
        />
    </div>
</template>

<script setup>
import { onMounted, ref, inject, computed, watch, nextTick } from 'vue'
import { useArenaOutputStore, useMoat2ProductStore } from '@/stores'
import { useRoute } from 'vue-router'
import { useI18n } from 'vue-i18n'
import {
    getCompetitors,
    getCompetitorsTimeline,
    getCompetitorsBreakdown,
} from '@/api/productAlignment.js'

import OutputCompetitorTable from './OutputCompetitorTable'
import OutputCompetitorSelection from './OutputCompetitorSelection'
import TrendingDataViolin from './charts/TrendingDataViolin'
import TimelineXYChartAM from './charts/Timeline'

// Use CSS variables instead of SCSS imports for better consistency with design tokens
const chartColors = [
    'var(--color-data-teal)',
    'var(--color-data-plum)',
    'var(--color-data-blue)',
    'var(--color-data-raspberry)',
    'var(--color-data-orange)',
    'var(--color-data-yellow)',
    'var(--color-data-lime)',
    'var(--color-data-green)',
    'var(--color-data-magenta)',
    'var(--color-data-marine)',
    'var(--color-data-cobalt)',
    'var(--color-data-aqua)',
]

import { cloneDeep } from 'lodash-es'
import { debounce } from 'lodash-es'

const emit = defineEmits(['everything-done-loading'])
const eventBus = inject('eventBus')
const logger = inject('logger')
const { t } = useI18n()
const moat2ProductStore = useMoat2ProductStore()
const arenaOutputStore = useArenaOutputStore()
const route = useRoute()

const props = defineProps({
    type: {
        type: String,
        default: 'pa',
    },
    entityId: {
        type: [Number, String],
        default: undefined,
    },
    entityName: {
        type: String,
        default: '',
    },
    loadingJob: {
        type: Boolean,
        default: false,
    },
    loading: {
        type: Boolean,
        default: true,
    },
})

const topCompetitorColors = ref(chartColors)
const topCompetitors = ref([])
const topCompetitorsEntityPks = computed(() => {
    return topCompetitors.value.map((item) => item.aonEntityPk)
})
const activeGraph = ref('both')
const graphDropdownOptions = ref([
    {
        itemText: 'Both',
        iconOptions: {
            iconStyle: 'fas',
            iconColor: 'grey01',
            iconName: 'fa-sparkles',
        },
    },
    {
        itemText: 'Timeline',
        iconOptions: {
            iconStyle: 'fas',
            iconColor: 'grey01',
            iconName: 'fa-timeline',
        },
    },
    {
        itemText: 'Quadrant',
        iconOptions: {
            iconStyle: 'fas',
            iconColor: 'grey01',
            iconName: 'fa-grid-2',
        },
    },
])
const currentChartTab = ref(0)
const localGroupingSelections = ref(null)
const showCompetitorSelection = ref(false)

const competitorsLoading = ref(false)
const loadingViolin = ref(false)
const loadingTimeline = ref(false)
const competitorTableUpdater = ref(0)
const competitorViolinChartData = ref([])
const competitorTimelineChartData = ref([])
const competitorTableData = ref([])
const allCompetitorTableData = ref([])
const watchlistPkFilter = ref(null)
const techIds = computed(() => {
    return props.type === 'pa'
        ? [...moat2ProductStore.techAreaIdCollection]
        : [...arenaOutputStore.techAreaIdCollection]
})
const activeStore = props.type === 'pa' ? moat2ProductStore : arenaOutputStore
const activeStoreAlignmentPk = activeStore.alignmentPk

const everythingDoneLoading = computed(() => {
    return !competitorsLoading.value && !loadingViolin.value && !loadingTimeline.value
})
const groupByUltimateParent = computed(() => {
    return arenaOutputStore.groupByUltimateParent
})

watch(
    () => everythingDoneLoading.value,
    async (newVal, oldVal) => {
        if (newVal) {
            emit('everything-done-loading', true)
        }
        if (!newVal) {
            emit('everything-done-loading', false)
        }
    }
)

watch(
    () => arenaOutputStore.arenaOutputActiveTab,
    async (newVal, oldVal) => {
        if (
            newVal === 2 &&
            !setsAreEqual(
                arenaOutputStore.arenaOutputTechAreaSelectionsOld,
                arenaOutputStore.arenaOutputTechAreaSelections
            )
        ) {
            debouncedTechChange.cancel()
            debouncedTechChange()
        }
    }
)

watch(
    () => moat2ProductStore.productAlignmentActiveTab,
    async (newVal, oldVal) => {
        if (
            newVal === 2 &&
            !setsAreEqual(
                moat2ProductStore.productAlignmentTechAreaSelectionsOld,
                moat2ProductStore.productAlignmentTechAreaSelections
            )
        ) {
            debouncedTechChange.cancel()
            debouncedTechChange()
        }
    }
)

watch(
    () => arenaOutputStore.initLoad,
    async (newVal, oldVal) => {
        if (newVal) {
            debouncedTechChange.cancel()
            arenaOutputStore.initLoad = false
            debouncedTechChange()
        }
    }
)

watch(
    () => moat2ProductStore.initLoad,
    async (newVal, oldVal) => {
        if (newVal) {
            debouncedTechChange.cancel()
            moat2ProductStore.initLoad = false
            debouncedTechChange()
        }
    }
)

const refreshCompetitorTable = (params) => {
    topCompetitors.value = []
    watchlistPkFilter.value = params.watchlistPk
    getCompetitorData(techIds.value ? techIds.value : null, watchlistPkFilter.value)
}

const debouncedTechChange = debounce(() => {
    topCompetitors.value = []
    getCompetitorData(techIds.value ? techIds.value : null, watchlistPkFilter.value)
}, 200)

const getCompetitorData = async (newIds = false, watchlistPk = null) => {
    eventBus.emit('set-arena-space-loading', true)
    loadingViolin.value = true
    loadingTimeline.value = true
    competitorsLoading.value = true
    localGroupingSelections.value = newIds

    try {
        await getCompetitors(
            route.params.jobPk ? route.params.jobPk : moat2ProductStore.alignmentPk,
            newIds ? newIds : techIds.value,
            watchlistPk,
            groupByUltimateParent.value
        )
            .then((response) => {
                allCompetitorTableData.value = cloneDeep(response.data)
                moat2ProductStore.allCompetitorData = cloneDeep(response.data)
                arenaOutputStore.allCompetitorData = cloneDeep(response.data)

                if (
                    arenaOutputStore.targetCompany.length > 0 &&
                    !arenaOutputStore.targetCompany[0].name
                ) {
                    let targetCompany = arenaOutputStore.allCompetitorData.find(
                        (item) => item.aonEntityPk === arenaOutputStore.targetCompany[0].aonEntityPk
                    )
                    arenaOutputStore.targetCompany = [targetCompany]
                    arenaOutputStore.targetCompanyLoading = false
                }

                groomTableData(response.data)

                eventBus.emit('set-arena-space-loading', false)
                getCompetitorsByYear(localGroupingSelections.value)
                getCompetitorsCumulativePatentSummaryByYear(localGroupingSelections.value)
                competitorTableUpdater.value++
                competitorsLoading.value = false

                if (props.type === 'pa') {
                    moat2ProductStore.productAlignmentTechAreaSelectionsOld =
                        moat2ProductStore.productAlignmentTechAreaSelections
                } else {
                    arenaOutputStore.arenaOutputTechAreaSelectionsOld =
                        arenaOutputStore.arenaOutputTechAreaSelections
                }
            })
            .catch((error) => {
                // Catch if call is canceled
                eventBus.emit('set-arena-space-loading', true)
                loadingViolin.value = true
                loadingTimeline.value = true
                competitorsLoading.value = true
                logger.error(error)
            })
    } catch (err) {
        // catch if call fails
        logger.error(err)
    }
}

const getCompetitorsByYear = async (newIds = false) => {
    loadingViolin.value = true
    try {
        const { data } = await getCompetitorsBreakdown(
            route.params.jobPk ? route.params.jobPk : moat2ProductStore.alignmentPk,
            newIds ? newIds : techIds.value,
            moat2ProductStore.topCompetitorCollection
        )

        let sortedData = data.sort((a, b) =>
            a.filingYear == b.filingYear
                ? a.patentCount - b.patentCount
                : a.filingYear - b.filingYear
        )
        competitorViolinChartData.value = sortedData
    } catch (err) {
        logger.error(err)
    } finally {
        loadingViolin.value = false
    }
}

const getCompetitorsCumulativePatentSummaryByYear = async (newIds = false) => {
    loadingTimeline.value = true
    try {
        const { data } = await getCompetitorsTimeline(
            route.params.jobPk ? route.params.jobPk : moat2ProductStore.alignmentPk,
            newIds ? newIds : techIds.value,
            moat2ProductStore.topCompetitorCollection
        )
        competitorTimelineChartData.value = data.filter((item) =>
            moat2ProductStore.topCompetitorCollection.includes(item.aonEntityPk)
        )
    } catch (err) {
        logger.error(err)
    } finally {
        loadingTimeline.value = false
    }
}

const groomTableData = (data, topList = false) => {
    // change to "if target entity is not null"
    if ((props.type !== 'arenas') & (topCompetitors.value.length === 0)) {
        let targetEntityIndex = data.findIndex((entity) => entity.aonEntityPk === props.entityId)
        let returnedTop = data.slice(0, 8)
        let targetInTopList = returnedTop.find((entity) => entity.aonEntityPk === props.entityId)
        if (targetEntityIndex !== -1 && targetInTopList === undefined) {
            topCompetitors.value.push(data.find((entity) => entity.aonEntityPk === props.entityId))
        }
    }

    if (!topList) {
        data = data.slice(0, 20)
        topCompetitors.value.length === 1
            ? topCompetitors.value.push(data.slice(0, 7))
            : topCompetitors.value.push(data.slice(0, 8))
        topCompetitors.value = topCompetitors.value.flat()
    } else {
        topCompetitors.value = topList
    }
    moat2ProductStore.productAlignmentTopCompetitors = topCompetitors.value

    let mergedData = []
    for (let i = 0; i < data.length; i++) {
        let found = false
        for (let j = 0; j < topCompetitors.value.length; j++) {
            if (data[i].aonEntityPk === topCompetitors.value[j].aonEntityPk) {
                found = true
                break
            }
        }
        if (!found) {
            mergedData.push(data[i])
        }
    }
    mergedData = [...topCompetitors.value, ...mergedData]

    competitorTableData.value = mergedData.map((item) => {
        return {
            ...item,
            background:
                topCompetitorColors.value[
                    topCompetitors.value.findIndex(
                        (violinItem) => violinItem.aonEntityName === item.aonEntityName
                    )
                ],
            pinned: topCompetitors.value.find(
                (violinItem) => violinItem.aonEntityName === item.aonEntityName
            )
                ? true
                : false,
        }
    })
}

const refreshSpace = () => {
    groomTableData(allCompetitorTableData.value, moat2ProductStore.productAlignmentTopCompetitors)
    getCompetitorsByYear(localGroupingSelections.value)
    getCompetitorsCumulativePatentSummaryByYear(localGroupingSelections.value)

    setTimeout(() => {
        eventBus.emit('set-arena-space-loading', false)
    }, 1000)
}

const setActiveChartTab = (id) => {
    currentChartTab.value = id
    eventBus.emit('set-active-chart-tab', id)
}

const changeGraph = (type) => {
    if (type.itemText === 'Both') {
        activeGraph.value = 'both'
    }
    if (type.itemText === 'Timeline') {
        activeGraph.value = 'timeline'
    }
    if (type.itemText === 'Quadrant') {
        activeGraph.value = 'quadrant'
    }
}

const getLegendColor = (comp) => {
    const index = topCompetitors.value.findIndex(
        (violinItem) => violinItem.aonEntityName === comp.aonEntityName
    )
    const targetColor = topCompetitorColors.value[index] || 'var(--color-data-grey)'
    return `background: ${targetColor}`
}

const setsAreEqual = (s1, s2) => {
    return s1.size === s2.size && [...s1].every((x) => s2.has(x))
}
</script>

<style lang="scss" scoped>
.chart-container {
    min-height: 500px;
}

.chart-holder {
    position: relative;
    height: 500px;

    .violin-chart,
    .timeline-chart {
        width: 100%;
        height: 100%;
    }

    &:first-of-type:not(.display-none) {
        border-right: solid 1px var(--color-border-ui);
    }
}

.border-top {
    border-top: 1px solid var(--color-border-ui);
}

.table-holder {
    position: relative;
    min-height: 400px;
    width: 100%;
}

.chart-legend-chip {
    width: 16px;
    height: 16px;
    border-radius: 50%;
}

// Responsive adjustments
@media (max-width: 768px) {
    .chart-container {
        min-height: 350px;
        flex-direction: column;
    }

    .chart-holder {
        height: 350px;

        &:first-of-type:not(.display-none) {
            border-right: none;
            border-bottom: solid 1px var(--color-border-ui);
        }
    }
}

:deep(.table-aips-latest) {
    border: none !important;
    .aips-table-wrap {
        border: none !important;
    }
    .table-header {
        display: none;
    }

    th {
        pointer-events: none;

        &:first-of-type {
            pointer-events: all;
            border-radius: 4px 0 0 0;
        }

        &:last-of-type {
            border-radius: 0 4px 0 0;
        }
    }
}
</style>
